<template>
  <ActivityResultForm
    v-if="goal && activity"
    ref="form"
    :activity-object="_activity"
    :goal-object="goal"
    :is-edit="true"
    :is-draft="isDraft"
    :attachment-option="attachmentOption"
    @onClickSave="handleClickSave"
    @onClickDraft="updateDraft"
    @onClickDelete="handleClickDelete" />
</template>

<script>
import ActivityResultForm from '@/components/view/ActivityResultForm.vue'
import validate from '@/components/mixin/validate'
import confirmIfFormChanged from '@/components/mixin/confirmIfFormChanged'
export default {
  components: {
    ActivityResultForm
  },
  mixins: [
    validate,
    confirmIfFormChanged()
  ],
  data() {
    return {
      activity: null,
      goal: null,
      attachmentOption: false,
      isDraft: false
    }
  },
  computed: {
    memberId() {
      return this.$route.params.id
    },
    goalId() {
      return this.$route.params.goalId
    },
    activityId() {
      return this.$route.params.activityId
    },
    _activity() {
      let activity = {
        ...this.activity,
        result_count: 1,
      }
      if (this.goal.conditionSetting !== null) {
        const conditions = this.goal.conditionSetting.items.reduce((acc, key) => {
          if (this.activity.condition?.[key] == null) {
            acc[key] = null
            return acc
          }
          if (['sleepHours', 'studyHours'].includes(key)) {
            const splitValue = this.activity.condition[key].split(':')
            acc[key] = `${splitValue[0].padStart(2, '0')}:${splitValue[1]}`
          } else {
            acc[key] = this.activity.condition[key]
          }
          return acc
        }, {})
        activity.conditions = conditions
      }
      if (this.goal.bodyPainSetting?.isRecordBodyPain) {
        activity.bodyPain = this.activity.bodyPain?.text || ''
      }
      return activity
    }
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.isDraft = this.$route.query.isDraft
      // 原因不明だが、たまにqueryから取得したisDraftがstringの場合があるのでbooleanに変換
      if (typeof this.isDraft === 'string') {
        this.isDraft = this.isDraft === 'true'
      }

      this.$store.commit('startLoading')
      return Promise.all([this.fetchActivitiesOrDrafts(), this.fetchGoal(), this.fetchTenantInfo()])
        .catch((err) => {
          console.error(err)
          this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
        }).finally(() => {
          this.$store.commit('finishLoading')
        })
    },
    fetchActivitiesOrDrafts() {
      if (this.isDraft) {
        this.fetchDrafts()
      } else {
        this.fetchActivities()
      }
    },
    fetchActivities() {
      return this.$axios.get(`/activities/activity_results/?goal=${this.goalId}`, {
        params: {
          limit: 99999
        }
      }).then(async res => {
        this.activity = res.data.results.slice().find(a => a.id === this.activityId)
        const workAtArray = this.activity.workAt.split('T')
        this.activity.date = workAtArray[0]
        this.activity.time = workAtArray[1].slice(0, 5)
      }).catch((e) => {
        console.error(e)
        throw e
      })
    },
    fetchDrafts(){
      return this.$axios.get(`/goals/${this.goalId}/activity_drafts/`).then(async res => {
        this.activity = res.data.results.slice().find(a => a.id === this.activityId)
        const workAtArray = this.activity.workAt.split('T')
        this.activity.date = workAtArray[0]
        this.activity.time = workAtArray[1].slice(0, 5)
      }).catch((e) => {
        console.error(e)
        throw e
      })
    },
    fetchGoal() {
      return this.$axios.get(`/goals/${this.goalId}/`).then(res => {
        this.goal = res.data
      })
    },
    async fetchTenantInfo() {
      const tenantInfo = await this.$axios.get('/tenants/')
      this.attachmentOption = tenantInfo.data.attachmentOption
      return
    },
    async handleClickSave(formData) {
      if (this.isDraft) {
        await this.saveAndPublishAndDeleteDraft(formData)
        return
      }
      await this.updateActivity(formData)
    },
    updateActivity(requestBody) {
      this.$store.commit('startLoading')
      return this.$axios.patch(`/activities/activity_results/${this.activityId}/`, requestBody).then(() => {
        this.$store.commit('notify/set', {message: '活動を保存しました。'})
        this.$refs.form.$refs.observer.reset()
        this.canLeave = true
        this.$nextTick(() => {
          this.$router.push(`/members/${this.memberId}?goalId=${this.goalId}`)
        })
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      }).finally(() => {
        this.$store.commit('finishLoading')
      })
    },
    createActivity(requestBody){
      this.$store.commit('startLoading')
      this.$axios.post('/activities/activity_results/', requestBody).then(() => {
        this.$axios.delete(`/goals/${this.goalId}/activity_drafts/${this.activityId}/`)
        this.$router.push(`/members/${this.memberId}?goalId=${this.goalId}`)
        this.$store.commit('notify/set', {message: '活動を保存しました。'})
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      }).finally(() => {
        this.$store.commit('finishLoading')
      })
    },
    onClickCancel() {
      this.$router.push(`/members/${this.memberId}`)
    },
    handleClickDelete() {
      const activityType = this.isDraft ? '下書き' : '活動'
      if (!window.confirm(`${activityType}を削除してもよろしいですか?`)) return
      return this.isDraft ? this.deleteDraft() : this.deleteActivity()
    },
    deleteActivity(){
      this.$store.commit('startLoading')
      this.$axios.delete(`/activities/activity_results/${this.activityId}/`).then(() => {
        this.canLeave = true
        this.$nextTick(() => {
          this.$router.replace(`/members/${this.memberId}`)
        })
        this.$store.commit('notify/set', {message: '活動を削除しました。'})
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      }).finally(() => {
        this.$store.commit('finishLoading')
      })
    },
    deleteDraft(){
      this.$store.commit('startLoading')
      return this.$axios.delete(`/goals/${this.goalId}/activity_drafts/${this.activityId}/`).then(() => {
        this.canLeave = true
        this.$nextTick(() => {
          this.$router.replace(`/members/${this.memberId}`)
        })
        this.$store.commit('notify/set', {message: '下書きを削除しました。'})
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      }).finally(() => {
        this.$store.commit('finishLoading')
      })
    },
    updateDraft(requestBody) {
      this.$store.commit('startLoading')
      this.$axios.patch(`/goals/${this.goalId}/activity_drafts/${this.activityId}/`, requestBody).then((res) => {
        this.$store.commit('notify/set', {message: '下書きを保存しました。'})
        this.$refs.form.$refs.observer.reset()
        this.canLeave = true
        this.$nextTick(() => {
          this.$router.push(`/members/${this.memberId}?goalId=${res.data.goal}`)
        })
      }).catch(() => {
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      }).finally(() => {
        this.$store.commit('finishLoading')
      })
    },
    async saveAndPublishAndDeleteDraft(requestBody) {
      this.$store.commit('startLoading')
      // Draftを公開する場合、Draftを最新の状態で保存、Draftから活動記録を作成、Draftのデータを削除の３処理のエンドポイントを使う
      try {
        await this.$axios.patch(`/goals/${this.goalId}/activity_drafts/${this.activityId}/`, requestBody),
        await this.$axios.post('/activities/activity_results/publish_draft/', { activity_draft: this.activityId }),
        await this.$axios.delete(`/goals/${this.goalId}/activity_drafts/${this.activityId}/`)
        this.$store.commit('notify/set', {message: '活動を保存しました。'})
        this.$refs.form.$refs.observer.reset()
        this.canLeave = true
        this.$nextTick(() => {
          this.$router.push(`/members/${this.memberId}?goalId=${this.goalId}`)
        })
      } catch (e) {
        console.error(e)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
        return
      } finally {
        this.$store.commit('finishLoading')
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.edit-head{
  background-color: $l-blue;
    @media screen and (min-width:992px) {
    width: calc(100% - 422px);
    right: 0;
    left: auto;
  }
  &__icon{
    height: 22px;
    width: auto;
    @media screen and (max-width:320px) {
      height: 18px;
    }
  }
  &__heading{
    color: $white;
    font-size: $fs-lg;
    font-weight: bold;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
  &__activity{
    position: relative;
    font-size: $fs-nm;
    @media screen and (max-width:320px) {
      font-size: $fs-m;
    }
  }
}

.form-check {
  position:relative;
}

input[type="checkbox"] {
  display:none;
}

.form-check-label {
  display: block;
  // padding: 10px 10px 10px 40px;
  cursor:pointer;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: $white;
}

.form-check-label:after {
  content: "";
  width: 20px;
  height: 20px;
  background-position: center;
  background-repeat: no-repeat;
  position: absolute;
  top: 50%;
  margin: -10px 8px;
  background-color: $grey16;
  border-radius: 50%;
}

input[type="checkbox"]:checked + .form-check-label:after {
  background-color: $pink;
}
.form-info{
  color: $grey09;
  &__delete{
    text-decoration: none;
    font-weight: bold;
    color: $grey09;
  }
  &__icon{
    display: inline-block;
    vertical-align: top;
  }
  &__date{
    font-size: $fs-m;
  }
}

.general-form__btn-delete {
  color: $grey09;
  position: relative;
  padding: 0;
  line-height: 48px;
  &__icon{
    position:absolute;
    top:11px;
    left:-24px;
  }
}
</style>